import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import BankDetailsEditModal from '../components/BankDetailsEditModal';

const formatDate = (s) => s ? new Date(s).toLocaleDateString('en-ZA') : '—';

const STATUS_COLORS = { verified: '#27ae60', pending: '#e67e22', rejected: '#e74c3c' };

const logoSrc = (url) => url.startsWith('http') ? url : `${process.env.REACT_APP_API_URL || ''}${url}`;

const SchoolShopsPage = () => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [bankShop, setBankShop] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/school-shops');
      setShops(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load school shops.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteShop = async (shop) => {
    const ok = window.confirm(`Delete ${shop.name}?\n\n`
      + `• Its ${shop.listings_count} listing(s) and any staff invitations are deleted.\n`
      + `• The owner and ${Math.max(shop.staff_count - 1, 0)} staff member(s) keep their accounts, as ordinary users.\n`
      + '• Shops with orders cannot be deleted.\n\n'
      + 'This cannot be undone.');
    if (!ok) return;
    setBusyId(shop.id);
    setError('');
    setMessage('');
    try {
      const res = await api.delete(`/admin/school-shops/${shop.id}`);
      setMessage(res.data?.message || 'School shop deleted');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to delete school shop.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h2 style={{ margin: 0 }}>School Shops</h2>
          <p style={{ margin: '4px 0 0', color: '#7f8c8d', fontSize: '13px' }}>
            Every school second-hand shop, including pending and rejected applications. Approve new shops under Verify Sellers.
          </p>
        </div>
        <button onClick={load} style={{ padding: '8px 16px', cursor: 'pointer' }}>Refresh</button>
      </div>

      {error && <p style={{ color: 'red' }}>{error}</p>}
      {message && <p style={{ color: '#27ae60' }}>{message}</p>}

      {loading ? (
        <p>Loading...</p>
      ) : shops.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#666', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
          <p style={{ fontSize: '18px' }}>No school shops</p>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={th}>Shop</th>
              <th style={th}>Status</th>
              <th style={th}>Owner</th>
              <th style={th}>Staff</th>
              <th style={th}>Listings</th>
              <th style={th}>Orders</th>
              <th style={th}>Created</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {shops.map(shop => (
              <tr key={shop.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {shop.logo_url && (
                      <img src={logoSrc(shop.logo_url)} alt="" style={{ width: '32px', height: '32px', objectFit: 'contain', flexShrink: 0 }} />
                    )}
                    <div>
                      {shop.name}
                      <br /><span style={muted}>{[shop.town, shop.province].filter(Boolean).join(', ')}</span>
                    </div>
                  </div>
                </td>
                <td style={td}>
                  <span style={{ ...pill, backgroundColor: STATUS_COLORS[shop.status] || '#95a5a6' }}>{shop.status}</span>
                </td>
                <td style={td}>{shop.owner_name || '—'}<br /><span style={muted}>{shop.owner_email}</span></td>
                <td style={td}>{shop.staff_count}</td>
                <td style={td}>{shop.listings_count}</td>
                <td style={td}>{shop.orders_count}</td>
                <td style={td}>{formatDate(shop.created_at)}</td>
                <td style={td}>
                  <button
                    disabled={busyId !== null}
                    onClick={() => { setMessage(''); setError(''); setBankShop(shop); }}
                    style={{ ...btn, backgroundColor: '#16a085', color: '#fff', marginRight: '6px', marginBottom: '4px' }}
                  >
                    Change bank details
                  </button>
                  <button
                    disabled={busyId !== null || shop.orders_count > 0}
                    title={shop.orders_count > 0 ? 'Shops with orders cannot be deleted' : ''}
                    onClick={() => deleteShop(shop)}
                    style={{ ...btn, backgroundColor: shop.orders_count > 0 ? '#bdc3c7' : '#e74c3c', color: '#fff' }}
                  >
                    {busyId === shop.id ? 'Deleting...' : 'Delete'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {bankShop && (
        <BankDetailsEditModal
          shop={bankShop}
          current={bankShop}
          onClose={() => setBankShop(null)}
          onSaved={(msg) => { setBankShop(null); setMessage(msg); load(); }}
        />
      )}
    </div>
  );
};

const th = { padding: '10px 12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #ddd' };
const td = { padding: '10px 12px', verticalAlign: 'top' };
const muted = { color: '#888', fontSize: '12px' };
const pill = { display: 'inline-block', color: '#fff', borderRadius: '10px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 };
const btn = { padding: '4px 10px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' };

export default SchoolShopsPage;
